// The debug log (D-45…D-47): what the app did, kept in this tab's memory only
// (the last LOG_MAX events, gone when the tab closes), so a problem that
// already happened can be reported. Debug mode shows it and downloads it.
//
// Nothing from the document goes in: no file name, no text, no metadata.
// Events hold fixed words, numbers and PDF names; the only free text is the
// engine's messages, and those pass through scrub(), which cuts out every
// quoted part (the engine quotes all document text through quote()).
import type { Request, Response } from '../engine/worker/protocol.ts';
import type { DocProfile } from '../engine/check/profile.ts';

declare const __FLEXPDF_BUILD__: string;

export const LOG_MAX = 1000;

type Fields = Record<string, string | number | boolean | undefined | null | string[] | string[][] | Record<string, number>>;
export type LogEvent =
  | { t: number; kind: 'session'; build: string; browser: string; os: string; dpr: number; language: string; screen: string }
  | { t: number; kind: 'profile'; profile: DocProfile }
  | ({ t: number; kind: 'engine'; request: string; ms: number; outcome: 'done' | 'refused' | 'failed'; error?: string; stack?: string } & Fields)
  /** the app's own check (pdf.js) rejected a change, which was then taken back */
  | { t: number; kind: 'check'; change: string; problems: string[]; shapes?: string[][] }
  | { t: number; kind: 'ui'; action: string; detail?: Fields }
  | { t: number; kind: 'error'; message: string; stack?: string };

/** Spread over a union keeps each member's own fields (a plain Omit would merge them). */
type NewEvent = LogEvent extends infer E ? (E extends { t: number } ? Omit<E, 't'> : never) : never;

const events: LogEvent[] = [];
const start = Date.now();

export function log(e: NewEvent): void {
  events.push({ t: Date.now() - start, ...e } as LogEvent);
  if (events.length > LOG_MAX) events.splice(0, events.length - LOG_MAX);
}

export const logEvents = (): readonly LogEvent[] => events;
export function clearLog(): void {
  events.length = 0;
}

/**
 * A message without the document's words: every quoted part becomes "…" (a stray quote cuts the rest), and
 * subset font names (ABCDEF+Name) become <font>.
 */
export function scrub(message: string): string {
  let s = message.replace(/"[^"]*"/g, '\u0000');
  const stray = s.indexOf('"');
  if (stray >= 0) s = `${s.slice(0, stray)}\u0000`;
  s = s
    .replace(/“[^”]*”?/g, '\u0000')
    .replace(/\u0000/g, '"…"')
    .replace(/\b[A-Z]{6}\+[^\s,;:()"]+/g, '<font>');
  return s.length > 600 ? `${s.slice(0, 600)}…` : s;
}

/** Function names and places in FlexPDF's code (the message line is dropped: it is in `error`). */
export function scrubStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  const lines = stack
    .split('\n')
    .filter((l) => /^\s*at\s|@/.test(l))
    .map((l) => scrub(l.trim().replace(/[a-z][\w+.-]*:\/\/[^/\s)]*\//gi, '/')))
    .slice(0, 15);
  return lines.length ? lines.join('\n') : undefined;
}

export function logSession(): void {
  const ua = navigator.userAgent;
  const browser = /Edg\/(\d+)/.exec(ua) ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}` : /Firefox\/(\d+)/.exec(ua) ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}` : /Chrome\/(\d+)/.exec(ua) ? `Chrome ${/Chrome\/(\d+)/.exec(ua)![1]}` : /Version\/(\d+).*Safari/.exec(ua) ? `Safari ${/Version\/(\d+)/.exec(ua)![1]}` : 'other';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'other';
  log({ kind: 'session', build: typeof __FLEXPDF_BUILD__ === 'string' ? __FLEXPDF_BUILD__ : 'dev', browser, os, dpr: window.devicePixelRatio, language: navigator.language, screen: `${screen.width}x${screen.height}` });
}

const round = (n: number) => Math.round(n * 1000) / 1000;

/** What a request was, without its text or bytes. */
function requestFields(req: Request): Fields {
  const f: Fields = {};
  if ('pageIndex' in req) f.page = req.pageIndex;
  if ('toPage' in req) f.toPage = req.toPage;
  if ('spanIndex' in req) f.span = req.spanIndex;
  if ('imageIndex' in req) f.image = req.imageIndex;
  if ('text' in req) f.textLength = [...req.text].length;
  if ('fit' in req && req.fit) f.fit = req.fit;
  if ('force' in req && req.force) f.force = true;
  if ('password' in req) f.password = req.password !== undefined;
  if ('bytes' in req) f.bytes = req.bytes.byteLength;
  if ('target' in req) f.targets = [req.target.kind];
  if ('targets' in req) f.targets = req.targets.map((t) => t.kind);
  if ('action' in req) f.action = req.action.kind === 'move' ? `move ${round(req.action.dx)} ${round(req.action.dy)}` : req.action.kind;
  if ('transform' in req) f.transform = req.transform.map(round).join(' ');
  if ('words' in req) f.words = req.words?.length ?? 0;
  if ('cutouts' in req && req.cutouts) f.cutouts = req.cutouts.length;
  return f;
}

/** What came back, without text or bytes. */
function responseFields(res: Response): { outcome: 'done' | 'refused'; fields: Fields } {
  const f: Fields = {};
  let outcome: 'done' | 'refused' = 'done';
  if ('history' in res) f.history = `${res.history.undo}/${res.history.redo}`;
  if ('regions' in res) f.regions = res.regions.length;
  if ('forced' in res && res.forced) f.forced = res.forced.map(scrub);
  switch (res.type) {
    case 'open':
      Object.assign(f, { pages: res.pages.length, encrypted: res.encrypted, passwordState: res.password, restricted: res.restricted, viewOnly: res.viewOnly && scrub(res.viewOnly) });
      break;
    case 'spans':
      Object.assign(f, { spans: res.spans.length, lines: res.lines.length, images: res.images.length, locked: res.spans.filter((s) => s.locked).length, editLocked: res.spans.filter((s) => s.editLocked).length, invisible: res.spans.filter((s) => s.invisible).length });
      break;
    case 'plan':
      f.plan = res.plan.kind;
      if (res.plan.kind === 'refuse') {
        outcome = 'refused';
        f.reasons = res.plan.reasons.map(scrub);
        if (res.plan.forceable) f.forceable = true;
      }
      if (res.plan.kind === 'substitute') Object.assign(f, { missing: res.plan.missing.length, grade: res.plan.grade, widthMismatches: res.plan.widthMismatches });
      if (res.fit) Object.assign(f, { fitBy: round(res.fit.by), fitLimit: res.fit.limit, fitScale: round(res.fit.scale) });
      if (res.align) f.align = res.align;
      if (res.shapes) f.shapes = res.shapes;
      break;
    case 'objects':
    case 'object':
      if (res.layer) Object.assign(f, { layerDeleted: res.layer.deleted, layerKept: res.layer.kept.length });
      if (res.scan) Object.assign(f, { scanCodec: res.scan.codec, scanBytes: `${res.scan.before} → ${res.scan.after}` });
      break;
    case 'residuals':
      f.residuals = res.residuals.length;
      break;
    case 'copyObjects':
      f.count = res.count;
      break;
    case 'paste':
      f.pasted = res.pasted.length;
      break;
    case 'export':
      f.bytes = res.bytes.byteLength;
      break;
  }
  return { outcome, fields: f };
}

/** Called by the engine client when a request settles. Page reads that succeed are left out: there are many. */
export function logEngine(req: Request, ms: number, res?: Response, err?: Error & { engineStack?: string; shapes?: string[][]; forceable?: boolean }): void {
  if (res && (req.type === 'spans' || req.type === 'init' || req.type === 'profile' || req.type === 'shape')) return;
  const base = { kind: 'engine' as const, request: req.type, ms: Math.round(ms), ...requestFields(req) };
  if (err) log({ ...base, outcome: 'failed', error: scrub(err.message), stack: scrubStack(err.engineStack), shapes: err.shapes, forceable: err.forceable || undefined });
  else if (res) {
    const { outcome, fields } = responseFields(res);
    log({ ...base, ...fields, outcome });
  }
}

export function logCheck(change: string, problems: string[], shapes?: string[][]): void {
  log({ kind: 'check', change, problems: problems.slice(0, 10).map(scrub), shapes });
}

/** An error the app caught or nothing caught; the engine's own are logged with their request already. */
export function logError(e: unknown): void {
  const err = e instanceof Error ? e : new Error(String(e));
  if ('engineStack' in err) return;
  log({ kind: 'error', message: scrub(err.message), stack: scrubStack(err.stack) });
}

/** The log as a file to save (JSON). */
export function logFile(): Blob {
  const body = { format: 'flexpdf-debug-log', version: 1, started: new Date(start).toISOString(), saved: new Date().toISOString(), events };
  return new Blob([JSON.stringify(body, null, 1)], { type: 'application/json' });
}

// Debug mode (D-45): for this tab only, so it is never left on by accident.
const DEBUG_KEY = 'flexpdf-debug';
export function debugOn(): boolean {
  try {
    return sessionStorage.getItem(DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}
export function setDebugOn(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(DEBUG_KEY, '1');
    else sessionStorage.removeItem(DEBUG_KEY);
  } catch {
    // Storage blocked: debug mode lasts until the page reloads.
  }
  log({ kind: 'ui', action: on ? 'debug on' : 'debug off' });
}
